import React, { useState } from 'react';
import { TextField, IconButton, InputAdornment } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

export interface PasswordInputProps {
  error?: boolean;
  fullWidth?: boolean;
  helperText?: string;
  label?: string;
  margin?: 'none' | 'dense' | 'normal';
  name?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  value: string;
  /**
   * Browser autofill hint. Callers MUST set this explicitly:
   *   - "current-password" on login screens
   *   - "new-password" on registration / password-reset / change-password forms
   * Default is undefined so the browser falls back to its own heuristics; an
   * incorrect value (e.g. "current-password" on a registration form) is worse
   * than no value because it can corrupt the user's saved credentials.
   */
  autoComplete?: 'current-password' | 'new-password' | 'off';
  /**
   * id forwarded to the underlying input. Useful when an external label
   * needs an htmlFor target (e.g. a custom <label> outside the TextField).
   */
  id?: string;
  /**
   * aria-describedby forwarded to the input — for inline helper text or
   * password-requirement hints rendered outside this component.
   */
  'aria-describedby'?: string;
}

const PasswordInput: React.FC<PasswordInputProps> = ({
  error = false,
  fullWidth = true,
  helperText,
  label = 'Password',
  margin = 'normal',
  name = 'password',
  onChange,
  required = true,
  value,
  autoComplete,
  id,
  'aria-describedby': ariaDescribedBy,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <TextField
      error={error}
      fullWidth={fullWidth}
      helperText={helperText}
      label={label}
      margin={margin}
      name={name}
      id={id}
      onChange={onChange}
      required={required}
      type={showPassword ? 'text' : 'password'}
      value={value}
      // 1.3.5 Identify Input Purpose — propagate the caller's hint to the
      // underlying <input>. Stored separately on inputProps because MUI v5
      // surfaces it on the input element rather than the TextField wrapper.
      inputProps={{
        autoComplete,
        'aria-describedby': ariaDescribedBy,
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {/* The toggle is a real <button> via IconButton. aria-pressed
                communicates the show/hide state to AT (a toggle button is
                the correct ARIA pattern here, not a checkbox or switch).
                aria-label provides the accessible name since IconButton
                renders only an icon. */}
            <IconButton
              onClick={() => setShowPassword((s) => !s)}
              edge="end"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              type="button"
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

export default PasswordInput;
